// phi_harvest advisor — pre-harvest-interval "safe to harvest" signal
// (STOCK-MODULE-AUDIT-2026-07-04 §6.4). The chemical datasheet already carries
// pre_harvest_interval_days (mig 519) and every completed spray snapshots it
// onto spray_records (mig 228) — this closes the loop by warning, at pick
// capture, when a block is still inside a spray's withholding window.
//
// Boundary semantics match FarmComplianceBlockReport: the clear date is
// applied_at + PHI days, and the hold is ACTIVE while clear > pickDate — i.e.
// harvest is allowed ON the clear date. A pick dated exactly on the clear date
// gets a caution (the day-granular maths can't see spray/pick times).
//
// Advisory only (DSS contract): the chip never blocks the save — a nogo is a
// legal-exposure warning the operator can override, and the override is what
// the decision-event telemetry exists to record.

import type { AdvisorRecommendation } from '@/lib/decisionEvents'
import { sprayHoldApplies, type PlantingWindowFields } from '@/lib/plantings'
import { sprayLabelPhis, sprayPhi, type PhiLabelChemical } from '@/lib/phiBlockState'

export interface PhiSprayInput {
  spray_id:                  string
  applied_at:                string        // YYYY-MM-DD
  /** The spray's PHI by the shared rule (phiBlockState.sprayPhiDays): the
   *  record's own, else the longest label PHI among its products, else null. */
  pre_harvest_interval_days: number | null
  /** True when that PHI came from a product label, not the record (FM10 F1). */
  phi_from_label?:           boolean
  /** No record PHI, and some product in the tank has no label PHI: the known
   *  part (pre_harvest_interval_days) still holds, then the spray is unknown —
   *  never cleared (Jason's ruling, 4 Oct 2026). */
  phi_partly_unknown?:       boolean
  product_name:              string | null
  // Phase C — which planting on THIS block the spray targeted (the junction
  // row's own scope for a covered block; the record's for the primary).
  // NULL = whole block: holds every planting (conservative, never filtered).
  project_id:                string | null
}

// `addDaysYmd` now lives in src/lib/dates.ts (shared with invoice periods).
// Imported for local use AND re-exported, so existing PHI consumers that
// import it from here keep working unchanged.
import { addDaysYmd } from '@/lib/dates'
export { addDaysYmd }

/** YYYY-MM-DD the block is clear again, or null when the spray has no PHI. */
export function phiClearDate(applied_at: string, phi: number | null): string | null {
  if (phi == null || phi <= 0) return null
  return addDaysYmd(applied_at, phi)
}

/** Raw spray row as the two PostgREST queries return it (primary-block query,
 *  and the parent embedded through a spray_block_targets junction row). */
export interface PhiSprayRow {
  spray_id:                  string
  applied_at:                string
  pre_harvest_interval_days: number | null
  product_name_override:     string | null
  deleted_at?:               string | null
  project_id?:               string | null
  chemical_id?:              string | null
  chemicals:                 ({ product_name: string | null } & Partial<PhiLabelChemical>) | null
  /** Mix lines, for their label PHI (FM10 F1). A deleted line holds nothing;
   *  a line with no chemical_id is a free-typed product (unknown). */
  spray_record_chemicals?:   Array<{ deleted_at: string | null; chemical_id?: string | null; chemicals: PhiLabelChemical | null }> | null
}

/** Merge primary-block rows with junction-embedded rows — dedupe by spray_id,
 *  drop soft-deleted embedded parents — into the advisor's input shape. */
export function mergePhiSprayRows(
  primary: PhiSprayRow[],
  // Phase C: a junction row carries its OWN project_id — the spray's scope
  // ON THIS COVERED BLOCK, distinct from the parent record's (which scopes
  // the primary block). Thread it per row, never inherit from the parent.
  viaTargets: Array<{ project_id?: string | null; spray_records: PhiSprayRow | null }>,
): PhiSprayInput[] {
  const toInput = (r: PhiSprayRow, project_id: string | null): PhiSprayInput => {
    // FM10 F1: the map's and the compliance rollup's rule (phiBlockState.ts) —
    // the record's PHI, else the longest label PHI over the header product and
    // every live mix line; a product with no label PHI that is not an adjuvant
    // or nutrient leaves the spray partly unknown.
    const { days, unknown } = sprayPhi(r.pre_harvest_interval_days,
      sprayLabelPhis({ ...r, chemicals: r.chemicals ? { pre_harvest_interval_days: null, ...r.chemicals } : null }))
    return {
      spray_id:                  r.spray_id,
      applied_at:                r.applied_at,
      pre_harvest_interval_days: days,
      phi_from_label:            r.pre_harvest_interval_days == null && days != null,
      phi_partly_unknown:        unknown && days != null,
      product_name:              r.product_name_override ?? r.chemicals?.product_name ?? null,
      project_id,
    }
  }
  const rows = new Map<string, PhiSprayInput>()
  for (const r of primary) rows.set(r.spray_id, toInput(r, r.project_id ?? null))
  for (const t of viaTargets) {
    const r = t.spray_records
    // Primary wins on dedupe — the record and its live primary junction row
    // are kept equal by the mig-20261016222316 sync trigger, so this is a
    // tie, not a choice (a test pins it).
    if (r && !r.deleted_at && !rows.has(r.spray_id)) {
      rows.set(r.spray_id, toInput(r, t.project_id ?? null))
    }
  }
  return [...rows.values()]
}

const productName = (s: PhiSprayInput) => s.product_name ?? 'Unnamed product'

/** "+N more …" overflow line shared by the reason builders. */
const overflowLine = (extra: number, noun: string) =>
  extra > 0 ? [`+${extra} more ${noun}${extra === 1 ? '' : 's'}`] : []

function unknownPhiReasonLines(unknown: PhiSprayInput[]): string[] {
  return unknown.slice(0, 3).map(s => s.phi_partly_unknown
    ? `${productName(s)} sprayed ${s.applied_at} — a product in the mix has no PHI on record — check the label`
    : `${productName(s)} sprayed ${s.applied_at} has no PHI on record — check the label`,
  ).concat(overflowLine(unknown.length - 3, 'spray with no PHI on record'))
}

function activeHoldReasonLines(active: Array<PhiSprayInput & { clear: string }>): string[] {
  return active.slice(0, 3).map(s =>
    `${productName(s)} sprayed ${s.applied_at} — PHI ${s.pre_harvest_interval_days}d${s.phi_from_label ? ' (from the label)' : ''}, clear from ${s.clear}`,
  ).concat(overflowLine(active.length - 3, 'active spray'))
}

export function assessPhiSafeToHarvest(
  sprays: PhiSprayInput[],
  pickDate: string,
  // Phase C — the pick's planting. NULL/omitted = a whole-block pick, which
  // inherits EVERY hold on the block (it harvests every planting's fruit).
  pickProjectId: string | null = null,
  // Windows for the coexistence test (sprayHoldApplies): without them every
  // explicit-vs-explicit pair is treated as the SAME ground (conservative).
  plantingWindows: ReadonlyMap<string, PlantingWindowFields> | null = null,
): AdvisorRecommendation | null {
  if (!pickDate) return null

  // Phase C planting scope, applied in the pure fn (not the query) so the
  // block-wide answer stays available and the fallback is testable: a spray
  // drops out only when both sides name plantings that provably occupy
  // different ground (concurrent windows) — see sprayHoldApplies.
  const scoped = sprays.filter(s => sprayHoldApplies(s.project_id, pickProjectId, plantingWindows))

  // A spray applied after the pick date can't hold that pick (backfilled
  // picks; L44) — belt-and-braces with the caller's query upper bound.
  const inWindow = scoped.filter(s => s.applied_at <= pickDate)

  const withClear = inWindow
    .map(s => ({ ...s, clear: phiClearDate(s.applied_at, s.pre_harvest_interval_days) }))
    .filter((s): s is PhiSprayInput & { clear: string } => s.clear != null)

  const active   = withClear.filter(s => s.clear > pickDate)
    .sort((a, b) => b.clear.localeCompare(a.clear))
  const held = new Set(active.map(s => s.spray_id))

  // spray-b-02: a NULL PHI is an UN-CATALOGUED product, not a cleared one —
  // dropping these rows let a 2-day-old spray of an unlisted chemical read as
  // green "PHI clear". A recorded 0 stays a real zero-day interval (go). A
  // partly-unknown spray is unknown once its known part no longer holds.
  const unknown = inWindow
    .filter(s => s.pre_harvest_interval_days == null || (s.phi_partly_unknown && !held.has(s.spray_id)))
    .sort((a, b) => b.applied_at.localeCompare(a.applied_at))
  const boundary = withClear.filter(s => s.clear === pickDate)

  const unknownReasons = unknownPhiReasonLines(unknown)

  if (active.length > 0) {
    return {
      verdict: 'nogo',
      reasons: activeHoldReasonLines(active).concat(unknownReasons),
      evidence: {
        pickDate,
        sprayCount:  scoped.length,
        scopedOutSprays: sprays.length - scoped.length,
        activeHolds: active.length,
        latestClear: active[0].clear,
        unknownPhi:  unknown.length,
        pickProjectId,
        // FM03 UF-03: the spray that holds the block longest — the chip opens it.
        holdingSprayId: active[0].spray_id,
      },
    }
  }

  if (unknown.length > 0 || boundary.length > 0) {
    return {
      verdict: 'caution',
      reasons: unknownReasons.concat(boundary.slice(0, 3).map(s =>
        `${productName(s)} PHI clears exactly on ${s.clear} — confirm the full interval has elapsed`,
      )),
      evidence: {
        pickDate,
        sprayCount:     scoped.length,
        scopedOutSprays: sprays.length - scoped.length,
        boundaryClears: boundary.length,
        unknownPhi:     unknown.length,
        pickProjectId,
        // FM03 UF-03: the newest spray whose PHI is unknown (else the one
        // clearing today) — the chip opens it.
        holdingSprayId: (unknown[0] ?? boundary[0])?.spray_id ?? null,
      },
    }
  }

  const cleared = withClear.map(s => s.clear).sort()
  const scopedOut = sprays.length - scoped.length
  return {
    verdict: 'go',
    reasons: cleared.length > 0
      ? [`No active pre-harvest interval for ${pickDate} (latest cleared ${cleared[cleared.length - 1]})`]
      // Never claim a block is spray-free when holds were merely scoped to
      // another planting — the audit trail must say what was set aside.
      : scopedOut > 0
        ? [`No sprays apply to this planting — ${scopedOut} spray${scopedOut === 1 ? '' : 's'} on this block belong${scopedOut === 1 ? 's' : ''} to other plantings`]
        : ['No sprays with a pre-harvest interval recorded for this block'],
    evidence: { pickDate, sprayCount: scoped.length, scopedOutSprays: scopedOut, activeHolds: 0, unknownPhi: 0, pickProjectId },
  }
}
